import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MailOpen, Paperclip } from 'lucide-react';
import { API, auth, err } from '../invoices/invoiceApi';
import { EmailPreviewPane } from './EmailPreviewPane';
import { LogoInUse } from '../company/LogoInUse';

const BrandCard = ({ brand, sender }) => (
  <div className="bg-white border rounded-lg p-4 text-sm grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="email-preview-brand">
    <p className="sm:col-span-2 font-semibold mb-1">Company Profile details used in emails</p>
    {['name', 'phone', 'email', 'address', 'website'].map((k) => (
      <p key={k} data-testid={`email-preview-brand-${k}`}><span className="text-neutral-500 capitalize">{k}: </span>{brand[k] || <span className="text-red-600">Missing</span>}</p>
    ))}
    <div className="sm:col-span-2 mt-2" data-testid="email-preview-brand-logo">{brand.logo_url ? <LogoInUse url={brand.logo_url} testId="email-preview-logo-in-use" /> : <p><span className="text-neutral-500">Logo: </span><span className="text-red-600">Missing</span></p>}</div>
    <p className="sm:col-span-2 text-xs text-neutral-500 mt-2" data-testid="email-preview-sender">
      Sender: {sender.provider === 'resend' ? `Resend, ${sender.from_name} <${sender.sender_email}>` : sender.provider === 'emergent' ? 'Platform sender' : 'No active sender (test emails cannot be sent)'}
    </p>
  </div>
);

const TemplateList = ({ groups, active, onPick }) => (
  <nav className="bg-white border rounded-lg p-2 space-y-3 max-h-[70vh] overflow-y-auto" data-testid="email-preview-list">
    {Object.entries(groups).map(([cat, items]) => (
      <div key={cat}>
        <p className="px-2 text-[11px] uppercase tracking-wider text-neutral-400">{cat}</p>
        {items.map((t) => (
          <button key={t.key} type="button" onClick={() => onPick(t.key)} data-testid={`email-preview-item-${t.key.replace(':', '-')}`}
            className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-1.5 transition-colors ${active === t.key ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100'}`}>
            <span className="flex-1 truncate">{t.label}</span>{t.pdf_logo && <Paperclip className="w-3 h-3 opacity-60" />}
          </button>
        ))}
      </div>
    ))}
  </nav>
);

export default function EmailPreviewTab({ token }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`${API}/admin/email-preview`, auth(token)).then((r) => { setData(r.data); setActive(r.data.templates[0]?.key); }).catch((e) => setError(err(e, 'Could not load email templates')));
  }, [token]);
  const groups = useMemo(() => (data?.templates || []).reduce((g, t) => ({ ...g, [t.category]: [...(g[t.category] || []), t] }), {}), [data]);
  if (error) return <p className="text-sm text-red-600" data-testid="email-preview-error">{error}</p>;
  if (!data) return <p className="text-sm text-neutral-500" data-testid="email-preview-loading">Loading…</p>;
  const meta = data.templates.find((t) => t.key === active);
  return (
    <div className="space-y-4" data-testid="email-preview-tab">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2"><MailOpen className="w-5 h-5" />Email Preview</h2>
        <p className="text-sm text-neutral-500">Every customer email, shown with sample data and your current Company Profile. Nothing is sent unless you send a test.</p>
      </div>
      <BrandCard brand={data.brand} sender={data.sender} />
      <div className="grid lg:grid-cols-[260px_1fr] gap-4">
        <TemplateList groups={groups} active={active} onPick={setActive} />
        {meta && <EmailPreviewPane token={token} meta={meta} me={data.me} canSend={data.sender.provider !== 'none'} />}
      </div>
    </div>
  );
}
